import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useQueryClient } from '@tanstack/react-query'
import { CircularProgress } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import SydButton from '../../../../../commonDesign/Button'
import StatusReportStatusIcon from '../../../../../organisms/StatusReport/StatusReportStatusIcon'
import { useRemoveImageAssociation } from '../../../../../../api/media'
import Icon from '../../../../../atoms/Icon'
import SydModal from '../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import {
  useGetStatusReportStatusesImagePurpose,
  useStatusIcon,
  useUpdateStatusReportStatusMutation
} from '../../../../../../api/groups'
import { QUERY_KEYS } from '../../../../../../api/queryKeys'
import SydInput from '../../../../../commonDesign/SydInput'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import LibrarySelector from './LibrarySelector'
import LibraryUploader from './LibraryUploader'

function StatusReportStatusEditModal ({ open, status, onClose }) {
  const queryClient = useQueryClient()
  const [view, setView] = useState(null)

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      displayName: '',
      color: '',
      codeName: ''
    }
  })

  const { data: currentImage, isFetching: isLoadingIcon } = useStatusIcon(status?.statusId)
  const { data: purpose, isLoading: isLoadingPurpose } = useGetStatusReportStatusesImagePurpose()
  const {
    mutateAsync: removeAssociation,
    isLoading: isRemovingAssociation
  } = useRemoveImageAssociation()
  const {
    mutateAsync: updateStatus,
    isLoading: isUpdatingStatus
  } = useUpdateStatusReportStatusMutation()

  const hasCustomIcon = !!currentImage

  // Reset form when status changes
  useEffect(() => {
    if (status) {
      form.reset({
        displayName: status.displayName || '',
        color: status.color || '',
        codeName: status.codeName || ''
      })
    }
  }, [status, form])

  const handleRemoveCustomIcon = useCallback(async (imageId) => {
    if (!purpose) return

    await removeAssociation({
      purposeId: purpose?.purposeId,
      levelTypeId: 279,
      levelId: status.statusId,
      imageId
    })

    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.statusReportStatuses, status.statusId] })
  }, [status, purpose, removeAssociation, queryClient])

  const handleSaveChanges = useCallback(async (data) => {
    await updateStatus({
      statusId: status.statusId,
      displayName: data.displayName,
      color: data.color,
      codeName: data.codeName
    })
    form.reset(data)
  }, [status, updateStatus, form])

  const isLoading = useMemo(() => isLoadingIcon || isLoadingPurpose || isRemovingAssociation || isUpdatingStatus, [isLoadingIcon, isLoadingPurpose, isRemovingAssociation, isUpdatingStatus])

  const displayName = form.watch('displayName')
  const color = form.watch('color') || status?.color

  if (!status) return null

  return (
    <SydModal open={open} onClose={onClose} title={`Manage Status: ${status.displayName}`} maxWidth='md'>
      {isLoadingPurpose ? (
        <CircularProgress />
      ) : (
        <>
          <div className='flex flex-col gap-xl'>
            <form onSubmit={form.handleSubmit(handleSaveChanges)} className='flex flex-col gap-md'>
              <div className='flex flex-col gap-md'>
                <Controller
                  name='displayName'
                  control={form.control}
                  rules={{ required: { value: true, message: 'Display name is required' } }}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Display Name'
                      description='The display name is the name shown to users for this status.'
                      error={hookFormErrorAdapter(form, fieldState)}
                    >
                      <SydInput
                        {...field}
                        size='sm'
                        fullWidth
                        error={fieldState.error}
                      />
                    </SydLabel>
                  )}
                />
                <Controller
                  name='color'
                  control={form.control}
                  rules={{
                    required: { value: true, message: 'Color is required' }
                  }}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Color'
                      description='Color used for the status icon background and text. Use a standard color format like hex or rgb (e.g., #000000 / rgb(0,0,0).'
                      error={hookFormErrorAdapter(form, fieldState)}
                    >
                      <SydInput
                        {...field}
                        size='sm'
                        fullWidth
                        placeholder='#000000'
                        error={fieldState.error}
                      />
                    </SydLabel>
                  )}
                />
                <Controller
                  name='codeName'
                  control={form.control}
                  render={({ field }) => (
                    <SydLabel
                      label='Code Name'
                      description='The code name is a unique identifier for the status and cannot be changed.'
                    >
                      <SydInput
                        {...field}
                        size='sm'
                        fullWidth
                        disabled
                      />
                    </SydLabel>
                  )}
                />
              </div>
              {form.formState.isDirty && (
                <SydButton
                  type='submit'
                  variant='primary'
                  disabled={isLoading || !form.formState.isValid}
                  processing={isUpdatingStatus}
                >
                  Save Changes
                </SydButton>
              )}
            </form>

            <hr className='w-full' />

            <div className='flex gap-md gap-lg items-center'>
              <div className='flex flex-col gap-md rounded bg-neutral-200 border border-neutral-300 items-center p-2 justify-center'>
                <div className='flex flex-col gap-sm items-center justify-center'>
                  <div className='font-size-xs'>Current Icon</div>
                  <div>
                    <StatusReportStatusIcon
                      statusId={status.statusId}
                      status={{ ...status, color, displayName }}
                      size={80}
                      useIcon
                      hasBackground
                    />
                  </div>
                </div>
                <div className='flex-1 flex gap-sm items-center'>
                  Default icon:
                  <span
                    className='font-size-sm flex items-center justify-center relative'
                    style={{ width: '24px', height: '24px', color: color || status.color }}
                  >
                    <div
                      className='absolute top-0 left-0 w-full h-full rounded opacity-lowlighted'
                      style={{ backgroundColor: color || status.color }}
                    />
                    {status.icon?.length ? <Icon name={status.icon} size={18} /> : '-'}
                  </span>
                </div>
              </div>

              {!view?.length && (
                <div className='flex flex-col gap-2 item-center justify-center flex-1'>
                  {hasCustomIcon ? (
                    <SydButton
                      variant='primary'
                      priority='warning'
                      onClick={() => handleRemoveCustomIcon(currentImage.imageId)}
                      disabled={isLoading}
                      processing={isRemovingAssociation}
                    >
                      Remove Custom Icon
                    </SydButton>
                  ) : (
                    <>
                      <SydButton variant='primary' onClick={() => setView('upload')} disabled={isLoading}>
                        Upload New Icon
                      </SydButton>

                      <div className='flex gap-2 items-center'>
                        <hr className='flex-1' />
                        <span className='text-secondary'>or</span>
                        <hr className='flex-1' />
                      </div>

                      <SydButton variant='primary' onClick={() => setView('library')} disabled={isLoading}>
                        Select from Library
                      </SydButton>
                    </>
                  )}
                </div>
              )}

              {view === 'upload' && (
                <LibraryUploader
                  statusId={status.statusId}
                  purposeId={purpose?.purposeId}
                  onClose={() => setView(null)}
                />
              )}

              {view === 'library' && (
                <LibrarySelector
                  statusId={status.statusId}
                  purposeId={purpose?.purposeId}
                  onClose={() => setView(null)}
                />
              )}
            </div>
          </div>

          <SydModalActions>
            <SydButton onClick={onClose}>
              Close
            </SydButton>
          </SydModalActions>
        </>
      )}
    </SydModal>
  )
}

StatusReportStatusEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  status: PropTypes.shape({
    codeName: PropTypes.string.isRequired,
    statusId: PropTypes.number.isRequired,
    displayName: PropTypes.string.isRequired,
    color: PropTypes.string,
    icon: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired
}

export default StatusReportStatusEditModal
